// Parent feis registration (MC2 card 9a9e350b, Devin 2026-10-16/17): the
// screen's layout and selection rules, kept free of React so the web and the
// mobile app can share one blueprint and so they can be tested directly.
//
// Inputs come from three reads:
//   GET /feis/:id                          the event
//   GET /account/dancers?feisId=           the family's dancers, with
//                                          isRegistered, registrationId and
//                                          lastLevel (highest level at their
//                                          last feis)
//   GET /feis/levelwise/competitions/:id   { result: {group: [comp]}, groups }
//                                          where each comp carries `layout`
//                                          (kind, level, forLevels, mainIds)
//
// Per dancer the syllabus becomes a row of "pages" (the level carousel): one
// per level they can enter, in ladder order, then Teams, then Just for fun.
// Each level page lists its dances, a second chance under its main dance, and
// the specials for that level. Championship dancers (Prelim/Open last time)
// don't see grade levels when the feis has a championship level for them.
import { agesCode } from "../../helper/DataArrays";
import { formatDate } from "../../helper/formatDate";
import { sortCompetitions } from "../../helper/SortSyllabus";

export const LEVEL_ORDER = [
  "FIRST_FEIS",
  "BEGINNER",
  "ADVANCED_BEGINNER",
  "NOVICE",
  "PRIZEWINNER",
  "PRELIMINARY_CHAMPIONSHIPS",
  "OPEN_CHAMPIONSHIPS",
  "ADULT",
  "ADULT_CHAMPIONSHIP",
];
const GRADE_LEVELS = new Set(["FIRST_FEIS", "BEGINNER", "ADVANCED_BEGINNER", "NOVICE", "PRIZEWINNER"]);
const CHAMP_LEVELS = new Set(["PRELIMINARY_CHAMPIONSHIPS", "OPEN_CHAMPIONSHIPS"]);
const ADULT_LEVELS = new Set(["ADULT", "ADULT_CHAMPIONSHIP"]);
const rankOf = (level) => {
  const i = LEVEL_ORDER.indexOf(level);
  return i === -1 ? LEVEL_ORDER.length : i;
};

const SMALL_WORDS = new Set(["and", "of", "the", "or", "na", "a"]);
/** "ADVANCED BEGINNER" -> "Advanced Beginner", "TIR NA NOG" -> "Tir na Nog". */
export const titleCase = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w, i) => (i > 0 && SMALL_WORDS.has(w) ? w : w.replace(/(^|[-/(])([a-zà-ÿ])/g, (m, p, c) => p + c.toUpperCase())))
    .join(" ")
    .replace(/\bSt\. Patrick'S\b/g, "St. Patrick's")
    .replace(/'S\b/g, "'s");

/** The birth year of a dancer ("2016", "2016-04-02"), or NaN. */
export const birthYearOf = (dancer) => Number(String(dancer?.birthday || "").slice(0, 4));

/**
 * The syllabus age codes a dancer dances in at this feis ("U10", or several
 * for adults), using the app's existing rule: feis year minus birth year,
 * kept within 5..23.
 */
export const ageCodesFor = (dancer, feisDate) => {
  const year = birthYearOf(dancer);
  if (!feisDate || !Number.isFinite(year) || year < 1900) return [];
  const age = formatDate(feisDate).getFullYear() - year;
  const clamped = Math.min(23, Math.max(5, age));
  return agesCode.find((a) => a.key === String(clamped))?.value || [];
};

/** Boys-only and girls-only competitions (the `boys`/`girls` flags). */
export const genderAllows = (comp, dancer) => {
  const boysOnly = comp?.boys === true && comp?.girls !== true;
  const girlsOnly = comp?.girls === true && comp?.boys !== true;
  const g = String(dancer?.gender || "").toLowerCase();
  if (boysOnly) return g.startsWith("boy");
  if (girlsOnly) return g.startsWith("girl");
  return true;
};

const canEnter = (comp, ages, dancer) =>
  Array.isArray(comp?.ages) && comp.ages.some((a) => ages.includes(a)) && genderAllows(comp, dancer);

/** Organiser group names compared the server's way (helpers/registrationLevels.js). */
const normalizeGroup = (s) => String(s || "").toUpperCase().replace(/[._]/g, " ").replace(/\s+/g, " ").trim();

const kindOf = (comp, groups) => comp?.layout?.kind || groups?.[comp?.group]?.kind || "level";
const levelOf = (comp, groups) => comp?.layout?.level ?? groups?.[comp?.group]?.level ?? null;

/**
 * Everything one dancer can enter, as carousel pages.
 * @returns {{pages: Array, champ: boolean, firstFeis: boolean}}
 *   page = { key, kind: "level"|"teams"|"fun"|"specials", title, group, level,
 *            rows: [{comp, seconds: [comp]}], specials: [{comp, seconds}] }
 */
export const buildPages = ({ syllabus, groups, dancer, feisDate }) => {
  const ages = ageCodesFor(dancer, feisDate);
  const levelPages = new Map(); // group name -> page
  const teams = [];
  const fun = [];
  const specials = [];
  const order = Object.keys(syllabus || {});
  order.forEach((group) => {
    (syllabus[group] || []).forEach((comp) => {
      if (!canEnter(comp, ages, dancer)) return;
      const kind = kindOf(comp, groups);
      if (kind === "special") specials.push(comp);
      else if (kind === "team") teams.push(comp);
      else if (kind === "fun") fun.push(comp);
      else {
        if (!levelPages.has(group)) {
          levelPages.set(group, { key: `level:${group}`, kind: "level", title: titleCase(group), group, level: levelOf(comp, groups), comps: [] });
        }
        levelPages.get(group).comps.push(comp);
      }
    });
  });

  let levels = [...levelPages.values()].sort((a, b) => {
    const r = rankOf(a.level) - rankOf(b.level);
    return r !== 0 ? r : order.indexOf(a.group) - order.indexOf(b.group);
  });

  // Prelim/Open dancers don't see grade levels (Devin 2026-10-17), when the
  // feis has a championship level for them to enter.
  const champ = CHAMP_LEVELS.has(dancer?.lastLevel) && levels.some((p) => CHAMP_LEVELS.has(p.level));
  if (champ) levels = levels.filter((p) => !GRADE_LEVELS.has(p.level));

  // Adults see their adult levels first.
  const adult = ADULT_LEVELS.has(dancer?.lastLevel) || (!dancer?.lastLevel && ages.includes("19 AND OVER"));
  if (adult) {
    levels = [...levels.filter((p) => ADULT_LEVELS.has(p.level)), ...levels.filter((p) => !ADULT_LEVELS.has(p.level))];
  }

  // Rows: a second chance sits under the main dance it belongs to when the
  // dancer can enter that main dance on the same page (among its dances or
  // its specials); otherwise it is an ordinary row. `pool` is everything on
  // the page.
  const toRows = (comps, pool = comps) => {
    const ids = new Set(pool.map((c) => String(c._id)));
    const mainOf = (c) => (isSecondChance(c) ? (c?.layout?.mainIds || []).find((id) => ids.has(String(id))) : undefined);
    const mains = sortCompetitions(comps.filter((c) => !mainOf(c)));
    return mains.map((comp) => ({
      comp,
      seconds: pool.filter((c) => String(mainOf(c)) === String(comp._id)),
    }));
  };

  const placed = new Set();
  const pages = levels.map((p) => {
    // A special shows on the pages of the levels it is for: canonical levels,
    // or the organiser's own level names (forGroups); none = every level.
    const forThis = specials.filter((s) => {
      const forLevels = s?.layout?.forLevels || [];
      const forGroups = s?.layout?.forGroups || [];
      if (!forLevels.length && !forGroups.length) return true;
      return (p.level && forLevels.includes(p.level)) || forGroups.includes(normalizeGroup(p.group));
    });
    forThis.forEach((s) => placed.add(String(s._id)));
    const pool = [...p.comps, ...forThis];
    return { key: p.key, kind: "level", title: p.title, group: p.group, level: p.level, rows: toRows(p.comps, pool), specials: toRows(forThis, pool) };
  });
  // Specials whose levels have no page here go on a Specials page, except
  // the grade-only ones a Prelim/Open dancer had hidden with those levels.
  const gradeOnly = (s) => {
    const forLevels = s?.layout?.forLevels || [];
    return forLevels.length > 0 && !(s?.layout?.forGroups || []).length && forLevels.every((l) => GRADE_LEVELS.has(l));
  };
  const leftover = specials.filter((s) => !placed.has(String(s._id)) && !(champ && gradeOnly(s)));
  if (leftover.length) pages.push({ key: "specials", kind: "specials", title: "Specials", level: null, rows: toRows(leftover), specials: [] });
  if (teams.length) pages.push({ key: "teams", kind: "teams", title: "Teams", level: null, rows: toRows(teams), specials: [] });
  if (fun.length) pages.push({ key: "fun", kind: "fun", title: "Just for fun", level: null, rows: toRows(fun), specials: [] });

  return { pages, champ, firstFeis: !dancer?.lastLevel };
};

/**
 * The page a dancer starts on: their last level when this feis has it,
 * otherwise the highest level here at or below it, otherwise the first page.
 */
export const startPageKey = (pages, lastLevel) => {
  if (!pages.length) return null;
  const levelPages = pages.filter((p) => p.kind === "level");
  if (lastLevel) {
    const exact = levelPages.find((p) => p.level === lastLevel);
    if (exact) return exact.key;
    const below = levelPages.filter((p) => p.level && rankOf(p.level) <= rankOf(lastLevel));
    if (below.length) return below.reduce((a, b) => (rankOf(b.level) > rankOf(a.level) ? b : a)).key;
  }
  return pages[0].key;
};

/** A page's rows: its dances, then its specials (each with their second chances). */
const allRows = (page) => [...page.rows, ...page.specials];

/** Every competition id shown on a page (rows, their second chances, specials). */
export const pageCompIds = (page) =>
  [...new Set(allRows(page).flatMap((r) => [r.comp, ...r.seconds]).map((c) => String(c._id)))];

/** How many of a dancer's picks are on a page. */
export const pickedOnPage = (page, picked) => {
  const set = new Set((picked || []).map(String));
  return pageCompIds(page).filter((id) => set.has(id)).length;
};

/**
 * Which dance a level competition is ("REEL", "TRAD_SET"), so the same dance
 * isn't entered at two levels; null for specials, teams, just-for-fun, second
 * chances and anything not named after one dance (championships). St.
 * Patrick's Day is the beginner grades' traditional set.
 */
const DANCES = [
  [/TRAD(ITIONAL|\.)? ?SET|ST\.? ?PATRICK/, "TRAD_SET"],
  [/HORNPIPE/, "HORNPIPE"],
  [/TREBLE JIG|HEAVY JIG/, "TREBLE_JIG"],
  [/SLIP JIG/, "SLIP_JIG"],
  [/LIGHT JIG/, "LIGHT_JIG"],
  [/SINGLE JIG/, "SINGLE_JIG"],
  [/TREBLE REEL/, null],
  [/\bREEL\b/, "REEL"],
];
export const danceKey = (comp) => {
  if (!comp || (comp.layout?.kind && comp.layout.kind !== "level")) return null;
  if (isSecondChance(comp)) return null;
  const d = String(comp.description || "").toUpperCase().replace(/\s+/g, " ");
  if (/SECOND|2ND/.test(d)) return null;
  // a trophy or special is its own competition, entered alongside the dance
  // ("REEL" and "GIRLS REEL TROPHY" at First Feis, Peach City 2026)
  if (/TROPHY|SPECIAL/.test(d)) return null;
  const hit = DANCES.find(([re]) => re.test(d));
  return hit ? hit[1] : null;
};

/** Every level row on a dancer's pages, with its dance and its page's title. */
const danceRows = (pages) =>
  pages
    .filter((p) => p.kind === "level")
    .flatMap((p) => p.rows.map((row) => ({ row, key: danceKey(row.comp), title: p.title })))
    .filter((x) => x.key);

/**
 * Tick or untick a competition for one dancer. A second chance can only be
 * ticked with its main dance, and unticking the main dance also unticks its
 * second chance (Devin 2026-10-17). A dance is entered at one level only:
 * ticking Reel at Prizewinner moves a Reel picked at Novice (with its second
 * chance) to it (Devin 2026-10-17). Returns the new id list.
 */
export const togglePick = (pages, picked, compId) => {
  const id = String(compId);
  const set = new Set((picked || []).map(String));
  // A special can show on several pages, so a competition can head several
  // rows: gather its second chances (and a second chance's mains) from all.
  const rows = pages.flatMap(allRows);
  const secondsOf = rows.filter((r) => String(r.comp._id) === id).flatMap((r) => r.seconds);
  const mainsOf = rows.filter((r) => r.seconds.some((s) => String(s._id) === id)).map((r) => String(r.comp._id));
  if (set.has(id)) {
    set.delete(id);
    secondsOf.forEach((s) => set.delete(String(s._id)));
  } else {
    if (mainsOf.length && !mainsOf.some((m) => set.has(m))) return [...set];
    const dances = danceRows(pages);
    const key = dances.find((x) => String(x.row.comp._id) === id)?.key;
    if (key) {
      dances
        .filter((x) => x.key === key && String(x.row.comp._id) !== id)
        .forEach(({ row }) => {
          set.delete(String(row.comp._id));
          row.seconds.forEach((s) => set.delete(String(s._id)));
        });
    }
    set.add(id);
  }
  return [...set];
};

/**
 * Changing an entry: put the entry's competitions that no page offers this
 * dancer (another age group, entered on the old screens or by an organizer)
 * back on the page of their own group, ticked, so the parent sees them and
 * can untick them to remove them; a group with no page gets an "Other
 * entries" page at the end. A kept dance is like any other pick: ticking the
 * same dance offered for the dancer's age replaces it.
 * Returns { pages, kept: Set(compId) }.
 */
export const withKeptEntries = (pages, ids, index) => {
  const shown = new Set(pages.flatMap(pageCompIds));
  const missing = (ids || []).map(String).filter((id) => !shown.has(id) && index.has(id)).map((id) => index.get(id));
  if (!missing.length) return { pages, kept: new Set() };
  const out = pages.map((p) => ({ ...p, rows: [...p.rows], specials: [...p.specials] }));
  const other = { key: "kept", kind: "level", title: "Other entries", group: null, level: null, rows: [], specials: [] };
  const groupPage = (comp) => out.find((p) => p.group && normalizeGroup(p.group) === normalizeGroup(comp.group)) || other;
  // mains first, then each second chance under its main (kept or offered),
  // so the usual rules hold: it needs its main, and goes when the main goes
  missing.filter((c) => !isSecondChance(c)).forEach((comp) => groupPage(comp).rows.push({ comp, seconds: [] }));
  // its main: one the server links it to (looked for everywhere first), else
  // a kept main of the same group and dance (never an offered dance of
  // another age group by name alone)
  const keptIds = new Set(missing.map((c) => String(c._id)));
  const base = (c) => normalizeGroup(String(c.description || c.code).replace(SECOND_WORDS, ""));
  const linked = (main, second) => (second.layout?.mainIds || []).map(String).includes(String(main._id));
  const sameDance = (main, second) =>
    keptIds.has(String(main._id)) &&
    normalizeGroup(main.group) === normalizeGroup(second.group) &&
    base(main) === base(second);
  const attach = (second, isMain) => {
    for (const page of [...out, other]) {
      for (const list of [page.rows, page.specials]) {
        const i = list.findIndex((r) => isMain(r.comp, second));
        if (i >= 0) {
          list[i] = { ...list[i], seconds: [...list[i].seconds, second] };
          return true;
        }
      }
    }
    return false;
  };
  missing.filter(isSecondChance).forEach((second) => {
    if (attach(second, linked) || attach(second, sameDance)) return;
    groupPage(second).rows.push({ comp: second, seconds: [] });
  });
  // a second chance the dancer is offered on its own (its main isn't, for
  // her age) whose main is a kept one: under that main, so it goes with it
  const keptMains = missing.filter((c) => !isSecondChance(c));
  const moves = []; // [second chance, its kept main]
  out.forEach((page) =>
    [...page.rows, ...page.specials].forEach((r) => {
      if (!isSecondChance(r.comp) || r.seconds.length || keptIds.has(String(r.comp._id))) return;
      const main = keptMains.find((m) => linked(m, r.comp)) || keptMains.find((m) => sameDance(m, r.comp));
      // a special can sit on several pages: move each second chance once
      if (main && !moves.some(([m]) => String(m._id) === String(r.comp._id))) moves.push([r.comp, main]);
    })
  );
  const moving = new Set(moves.map(([second]) => String(second._id)));
  out.forEach((page) => {
    page.rows = page.rows.filter((r) => !moving.has(String(r.comp._id)));
    page.specials = page.specials.filter((r) => !moving.has(String(r.comp._id)));
  });
  moves.forEach(([second, main]) => attach(second, (comp) => String(comp._id) === String(main._id)));
  return { pages: other.rows.length ? [...out, other] : out, kept: new Set(missing.map((c) => String(c._id))) };
};

/**
 * For each unticked level dance whose dance is picked on another page, that
 * page's title, so the row can say "Picked at Novice". Map(compId -> title).
 */
export const dancePickedElsewhere = (pages, picked) => {
  const set = new Set((picked || []).map(String));
  const dances = danceRows(pages);
  const pickedAt = new Map();
  dances.forEach((x) => {
    if (set.has(String(x.row.comp._id))) pickedAt.set(x.key, x.title);
  });
  const out = new Map();
  dances.forEach((x) => {
    const id = String(x.row.comp._id);
    // (on its own page the picked row is right there: no note)
    if (!set.has(id) && pickedAt.has(x.key) && pickedAt.get(x.key) !== x.title) out.set(id, pickedAt.get(x.key));
  });
  return out;
};

/** The highest canonical level among some competitions (an entry's level), or null. */
export const highestLevel = (ids, index, groups) =>
  (ids || []).reduce((best, id) => {
    const c = index.get(String(id));
    const level = c && kindOf(c, groups) === "level" ? levelOf(c, groups) : null;
    if (!level) return best;
    return !best || rankOf(level) > rankOf(best) ? level : best;
  }, null);

/** Every competition the screen knows about, by id. */
export const compIndex = (syllabus) => {
  const map = new Map();
  Object.entries(syllabus || {}).forEach(([group, comps]) =>
    (comps || []).forEach((c) => map.set(String(c._id), { ...c, group }))
  );
  return map;
};

/**
 * What a dancer's picks read as in a summary: "Novice Reel, Light Jig" per
 * level, "Novice Reel · 2nd chance", "4-Hand team", a special by its name.
 * @returns {Array<{label: string, cents: number}>}
 */
export const summaryLines = (picked, index, groups) => {
  const byGroup = new Map();
  const extras = [];
  (picked || []).forEach((id) => {
    const c = index.get(String(id));
    if (!c) return;
    const cents = Math.round(Number(c.price || 0) * 100);
    const kind = kindOf(c, groups);
    const name = titleCase(c.description || c.code);
    // "ADULT BEGINNER / ADVANCED BEGINNER REEL" already names its level:
    // shown as it is, not as "Adult Adult Beginner …".
    const namesLevel = normalizeGroup(c.description).startsWith(normalizeGroup(c.group).split(" ")[0]);
    if (kind === "level" && !isSecondChance(c) && !namesLevel) {
      if (!byGroup.has(c.group)) byGroup.set(c.group, { names: [], cents: 0 });
      const g = byGroup.get(c.group);
      g.names.push({ name, comp: c });
      g.cents += cents;
    } else if (isSecondChance(c)) {
      extras.push({ label: `${titleCase(c.group)} ${name.replace(/[\s-]*second( chance)?/i, "")} · 2nd chance`, cents });
    } else if (kind === "team") {
      extras.push({ label: /team/i.test(name) ? name : `${name} team`, cents });
    } else {
      extras.push({ label: name, cents });
    }
  });
  const levelLines = [...byGroup.entries()]
    .sort((a, b) => rankOf(groups?.[a[0]]?.level) - rankOf(groups?.[b[0]]?.level))
    .map(([group, g]) => ({
      label: `${titleCase(group)} ${sortCompetitions(g.names.map((n) => n.comp)).map((c) => titleCase(c.description || c.code)).join(", ")}`,
      cents: g.cents,
    }));
  return [...levelLines, ...extras];
};

const SECOND_WORDS = /[\s-]*(second chance|second|2nd chance)\b/i;
// A second chance: linked to a main dance by the server, or flagged and named
// as one. A flagged competition with no main and no "second" in its name is a
// mislabel, e.g. an adult tier coded "-2" ("Adult Novice/Prizewinner - LIGHT
// JIG", East Coast Feis 2026): an ordinary dance.
const isSecondChance = (c) =>
  !!(c?.layout?.mainIds?.length || (c?.second_chance && SECOND_WORDS.test(String(c?.description || ""))));
const SECTION_RANK = { level: 0, special: 1, team: 2, fun: 3 };
const SECTION_TITLE = { special: "Specials", team: "Teams", fun: "Just for fun" };

/**
 * The review's layout of a dancer's picks (Devin 2026-10-17): a section per
 * level (in ladder order), then Specials, Teams and Just for fun; one row per
 * dance with its price, and a second chance on its own line right under its
 * dance (sub: true, "2nd chance"). A level's name isn't repeated on its
 * rows ("ADULT BEGINNER REEL" under Adult reads "Beginner Reel").
 * @returns {Array<{key: string, title: string, rows: Array<{id: string, name: string, cents: number, sub: boolean, secondOnly: boolean}>}>}
 *   sub = the second chance of the row above; secondOnly = a second chance whose dance isn't picked
 */
const sameAges = (a, b) => [...(a?.ages || [])].sort().join() === [...(b?.ages || [])].sort().join();
export const summarySections = (picked, index, groups) => {
  const comps = (picked || []).map((id) => index.get(String(id))).filter(Boolean);
  const cents = (c) => Math.round(Number(c.price || 0) * 100);
  const base = (c) => normalizeGroup(String(c.description || c.code).replace(SECOND_WORDS, ""));
  const mains = comps.filter((c) => !isSecondChance(c));
  // A second chance joins its main dance when that is picked too.
  const joined = new Map(); // main id -> second chance
  const loose = [];
  comps.filter(isSecondChance).forEach((s) => {
    const byId = (s.layout?.mainIds || []).map(String);
    const main =
      mains.find((m) => byId.includes(String(m._id)) && !joined.has(String(m._id))) ||
      // the same dance in the same age group first (a whole syllabus has a Reel per age)
      mains.find((m) => m.group === s.group && base(m) === base(s) && sameAges(m, s) && !joined.has(String(m._id))) ||
      mains.find((m) => m.group === s.group && base(m) === base(s) && !joined.has(String(m._id)));
    if (main) joined.set(String(main._id), s);
    else loose.push(s);
  });
  const sections = new Map();
  [...mains, ...loose].forEach((c) => {
    const kind = kindOf(c, groups);
    const key = kind === "level" ? `level:${c.group}` : kind;
    if (!sections.has(key)) {
      sections.set(key, {
        key,
        kind,
        title: kind === "level" ? titleCase(c.group) : SECTION_TITLE[kind] || titleCase(c.group),
        level: kind === "level" ? levelOf(c, groups) : null,
        comps: [],
      });
    }
    sections.get(key).comps.push(c);
  });
  return [...sections.values()]
    .sort((a, b) => SECTION_RANK[a.kind] - SECTION_RANK[b.kind] || rankOf(a.level) - rankOf(b.level))
    .map(({ key, kind, title, comps: list }) => {
      const lead = normalizeGroup(title).split(" ")[0];
      return {
        key,
        title,
        rows: sortCompetitions(list).flatMap((c) => {
          const second = joined.get(String(c._id));
          const raw = String(c.description || c.code);
          let name = titleCase(isSecondChance(c) ? raw.replace(SECOND_WORDS, "") : raw);
          if (kind === "level" && normalizeGroup(name).startsWith(`${lead} `)) name = name.slice(lead.length + 1);
          if (kind === "team" && !/team/i.test(name)) name = `${name} team`;
          const row = { id: String(c._id), name, cents: cents(c), sub: false, secondOnly: isSecondChance(c) };
          // its second chance on a line of its own, right under it
          return second ? [row, { id: String(second._id), name: "2nd chance", cents: cents(second), sub: true, secondOnly: false }] : [row];
        }),
      };
    });
};

export const money = (cents, currency) => {
  const v = (Number(cents || 0) / 100).toFixed(2);
  const trimmed = v.endsWith(".00") ? v.slice(0, -3) : v;
  return currency ? `${currency} $${v}` : `$${trimmed}`;
};

// ---------------------------------------------------------------------------
// Draft: the picks survive a refresh, Back from payment, or a closed tab for
// a week (per event, this browser). Dancers already entered and competitions
// no longer in the syllabus are dropped when it is read back.
// ---------------------------------------------------------------------------
const DRAFT_DAYS = 7;
const draftKey = (eventId) => `feisRegDraft:${eventId}`;

export const saveDraft = (eventId, picks) => {
  try {
    const any = Object.values(picks || {}).some((l) => l && l.length);
    if (!any) localStorage.removeItem(draftKey(eventId));
    else localStorage.setItem(draftKey(eventId), JSON.stringify({ savedAt: Date.now(), picks }));
  } catch (_) { }
};

export const loadDraft = (eventId, { dancers, index }) => {
  try {
    const raw = JSON.parse(localStorage.getItem(draftKey(eventId)) || "null");
    if (!raw || Date.now() - Number(raw.savedAt || 0) > DRAFT_DAYS * 86400000) return {};
    const open = new Set((dancers || []).filter((d) => !d.isRegistered).map((d) => String(d._id)));
    const out = {};
    Object.entries(raw.picks || {}).forEach(([dancerId, ids]) => {
      if (!open.has(dancerId)) return;
      const kept = (ids || []).map(String).filter((id) => index.has(id));
      if (kept.length) out[dancerId] = kept;
    });
    return out;
  } catch (_) {
    return {};
  }
};

/**
 * Take paid picks out of an event's draft (what one payment covered), leaving
 * anything picked since for another payment. `paid` = {dancerId: [compIds]}.
 */
export const removeFromDraft = (eventId, paid) => {
  try {
    const raw = JSON.parse(localStorage.getItem(draftKey(eventId)) || "null");
    if (!raw) return;
    const picks = { ...(raw.picks || {}) };
    Object.entries(paid || {}).forEach(([dancerId, ids]) => {
      const gone = new Set((ids || []).map(String));
      const left = (picks[dancerId] || []).filter((id) => !gone.has(String(id)));
      if (left.length) picks[dancerId] = left;
      else delete picks[dancerId];
    });
    saveDraft(eventId, picks);
  } catch (_) { }
};

export const clearDraft = (eventId) => {
  try {
    localStorage.removeItem(draftKey(eventId));
  } catch (_) { }
};
